import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Form, Input, Button, Upload, Select, Card, Typography, Divider, Row, Col, Checkbox } from "antd";
import { BankOutlined, UserOutlined, MailOutlined, PhoneOutlined, GlobalOutlined, LockOutlined, PlusOutlined, EnvironmentOutlined, FileTextOutlined, ApartmentOutlined } from "@ant-design/icons";
import { fetchDistricts } from "../../api/api";
import api from "../../api/config";
// import MapPicker from "../MapPicker";
import authApi from "../../api/authApi";
import { prepareFormData, validateFileType, validateFileSize } from "../../utils/formDataHelper";

const IncubatorRegister = ({ editingIncubator = null, isAdminEdit = false, selfEdit = false, onSuccess = null }) => {
  const [formData, setFormData] = useState({
    incubatorName: "",
    directorName: "",
    contactPerson: "",
    designation: "",
    email: "",
    phone: "",
    website: "",
    district: "",
    address: "",
    description: "",
    password: "",
    password2: "",
    logo: null,
    banner: null,
    latitude: 20.9374,
    longitude: 77.7796,
  });
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [errors, setErrors] = useState({});
  const [showMap, setShowMap] = useState(false);
  const navigate = useNavigate();

  const {
    incubatorName,
    directorName,
    contactPerson,
    designation,
    email,
    phone,
    website,
    district,
    address,
    description,
    password,
    password2,
  } = formData;

  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const response = await fetchDistricts();
        setDistricts(response.data || []);
      } catch (error) {
        console.error("Error fetching districts:", error);
        toast.error("Failed to load districts");
      }
    };
    loadDistricts();
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (editingIncubator && isAdminEdit) {
      setFormData({
        incubatorName: editingIncubator.incubatorName || "",
        directorName: editingIncubator.directorName || "",
        contactPerson: editingIncubator.contactPerson || "",
        designation: editingIncubator.designation || "",
        email: editingIncubator.email || "",
        phone: editingIncubator.phone || "",
        website: editingIncubator.website || "",
        district: editingIncubator.district || "",
        address: editingIncubator.address || "",
        description: editingIncubator.description || "",
        password: "",
        password2: "",
        logo: null,
        banner: null,
        latitude: editingIncubator.latitude || 20.9374,
        longitude: editingIncubator.longitude || 77.7796,
      });

      // Set logo preview
      if (editingIncubator.logo) {
        let logoUrl = editingIncubator.logo;
        if (!logoUrl.startsWith('http')) {
          if (logoUrl.startsWith('/')) {
            logoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${logoUrl}`;
          } else {
            logoUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${logoUrl}`;
          }
        }
        setLogoPreview(logoUrl);
      }

      // Set banner preview
      if (editingIncubator.banner) {
        let bannerUrl = editingIncubator.banner;
        if (!bannerUrl.startsWith('http')) {
          if (bannerUrl.startsWith('/')) {
            bannerUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${bannerUrl}`;
          } else {
            bannerUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${bannerUrl}`;
          }
        }
        setBannerPreview(bannerUrl);
      }
    }
  }, [editingIncubator, isAdminEdit]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    
    if ((name === 'logo' || name === 'banner') && files && files[0]) {
      const file = files[0];
      setFormData({
        ...formData,
        [name]: file,
      });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        if (name === 'logo') {
          setLogoPreview(reader.result);
        } else {
          setBannerPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  const handleLocationSelect = ({ latitude, longitude }) => {
    setFormData(prev => ({ ...prev, latitude, longitude }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!incubatorName.trim()) newErrors.incubatorName = 'Incubator name is required';
    if (!directorName.trim()) newErrors.directorName = 'Director name is required';
    if (!contactPerson.trim()) newErrors.contactPerson = 'Contact person is required';
    if (!designation.trim()) newErrors.designation = 'Designation is required';
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    if (!phone) newErrors.phone = 'Phone number is required';
    else if (!/^[0-9]{10}$/.test(phone)) newErrors.phone = 'Phone number must be 10 digits';
    if (!website) newErrors.website = 'Website is required';
    else if (!/^https?:\/\//.test(website)) newErrors.website = 'Website must start with http:// or https://';
    if (!district) newErrors.district = 'District is required';
    if (!address) newErrors.address = 'Address is required';
    if (!description) newErrors.description = 'Description is required';
    
    // Skip password validation in edit mode
    if (!isAdminEdit) {
      if (!password) newErrors.password = 'Password is required';
      else if (password.length < 6) newErrors.password = 'Password must be at least 6 characters';
      if (password !== password2) newErrors.password2 = 'Passwords do not match';
    }
    
    // File validations
    if (formData.logo) {
      if (!validateFileType(formData.logo, ['image/jpeg', 'image/png', 'image/gif'])) {
        newErrors.logo = 'Only JPG, PNG or GIF files are allowed';
      } else if (!validateFileSize(formData.logo, 5)) { // 5MB max
        newErrors.logo = 'Logo size should be less than 5MB';
      }
    }
    
    if (formData.banner) {
      if (!validateFileType(formData.banner, ['image/jpeg', 'image/png', 'image/gif'])) {
        newErrors.banner = 'Only JPG, PNG or GIF files are allowed';
      } else if (!validateFileSize(formData.banner, 10)) { // 10MB max for banner
        newErrors.banner = 'Banner size should be less than 10MB';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      if (isAdminEdit && editingIncubator) {
        // Update existing incubator
        const formDataToSend = new FormData();
        formDataToSend.append('incubatorName', incubatorName);
        formDataToSend.append('directorName', directorName || '');
        formDataToSend.append('contactPerson', contactPerson);
        formDataToSend.append('designation', designation || '');
        formDataToSend.append('email', email);
        formDataToSend.append('phone', phone);
        formDataToSend.append('website', website || '');
        formDataToSend.append('district', district || '');
        formDataToSend.append('address', address);
        formDataToSend.append('description', description || '');
        formDataToSend.append('latitude', formData.latitude.toString());
        formDataToSend.append('longitude', formData.longitude.toString());
        
        // Only append files if they were changed
        if (formData.logo) {
          formDataToSend.append('logo', formData.logo);
        }
        if (formData.banner) {
          formDataToSend.append('banner', formData.banner);
        }
        
        // Handle focusAreas and facilities if they exist in editingIncubator
        if (editingIncubator.focusAreas) {
          formDataToSend.append('focusAreas', JSON.stringify(editingIncubator.focusAreas));
        }
        if (editingIncubator.facilities) {
          formDataToSend.append('facilities', JSON.stringify(editingIncubator.facilities));
        }
        
        // Self-editing users hit the self-service route (auth-only),
        // admins hit the admin-only management route.
        const response = selfEdit
          ? await api.put(`/incubator/${editingIncubator._id}`, formDataToSend)
          : await api.put(`/admin/incubators/${editingIncubator._id}`, formDataToSend);
        toast.success('Incubator updated successfully');
        if (onSuccess) onSuccess();
      } else {
        // Register new incubator
        const formDataToSend = prepareFormData(formData);
        const response = await authApi.registerIncubator(formDataToSend);
        toast.success("Registration successful! Please log in.");
        navigate("/login");
      }
    } catch (error) {
      console.error("Registration/Update error:", error);
      
      // Handle validation errors from the server
      if (error.errors) {
        const errorMsgs = error.errors.map(err => err.msg).join("\n");
        toast.error(errorMsgs);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(error.message || "Operation failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Ant Design styling
  const baseStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#333",
  };

  const labelStyle = {
    fontFamily: "Rubik, sans-serif",
    fontSize: "15px",
    fontWeight: 400,
    color: "#333",
    marginBottom: "6px",
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <div style={{ padding: isAdminEdit ? "0" : "20px", maxWidth: "900px", margin: "0 auto" }}>
      {!isAdminEdit && (
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <Typography.Title level={2} style={{ ...baseStyle, fontSize: "28px", fontWeight: 500, color: "#470172" }}>
            Incubator Registration
          </Typography.Title>
          <Typography.Text style={{ ...baseStyle, fontSize: "16px", opacity: 0.7 }}>
            Register your incubator to join our network
          </Typography.Text>
        </div>
      )}

      <Card bordered style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(71, 1, 114, 0.10)", marginBottom: 32 }}>
        <form onSubmit={handleSubmit}>
        <Form layout="vertical" component={false} requiredMark={false}>
          {/* Logo Upload */}
          <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
            Incubator Logo <span style={{ color: 'red' }}>*</span>
          </Typography.Title>
          <Form.Item style={{ marginBottom: 20 }}>
            <Upload
              listType="picture-card"
              showUploadList={false}
              beforeUpload={() => false}
              accept="image/*"
              maxCount={1}
              onChange={(info) => {
                const file = info.fileList[0]?.originFileObj;
                if (file) {
                  handleChange({ target: { name: 'logo', files: [file] } });
                }
              }}
            >
              {logoPreview ? (
                <img src={logoPreview} alt="logo" style={{ width: '100%', height: 120, objectFit: 'contain', borderRadius: 8 }} />
              ) : (
                uploadButton
              )}
            </Upload>
            <Typography.Text style={{ fontSize: 12, color: '#999' }}>
              Upload incubator logo (Max 5MB)
            </Typography.Text>
            {errors.logo && <div style={{ color: 'red', marginTop: 4 }}>{errors.logo}</div>}
          </Form.Item>

          <Divider style={{ margin: "24px 0" }} />

          {/* Incubator Details */}
          <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
            Incubator Details
          </Typography.Title>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label={<span style={labelStyle}>Incubator Name <span style={{ color: 'red' }}>*</span></span>}>
                <Input 
                  prefix={<BankOutlined />} 
                  name="incubatorName"
                  value={incubatorName}
                  onChange={handleChange}
                  placeholder="Enter incubator name"
                  required
                  style={{ borderRadius: 6 }}
                />
                {errors.incubatorName && <div style={{ color: 'red', marginTop: 4 }}>{errors.incubatorName}</div>}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={<span style={labelStyle}>Director's Name <span style={{ color: 'red' }}>*</span></span>}>
                <Input 
                  prefix={<UserOutlined />} 
                  name="directorName"
                  value={directorName}
                  onChange={handleChange}
                  placeholder="Director's full name"
                  required
                  style={{ borderRadius: 6 }}
                />
                {errors.directorName && <div style={{ color: 'red', marginTop: 4 }}>{errors.directorName}</div>}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={<span style={labelStyle}>Contact Person <span style={{ color: 'red' }}>*</span></span>}>
                <Input 
                  prefix={<UserOutlined />} 
                  name="contactPerson"
                  value={contactPerson}
                  onChange={handleChange}
                  placeholder="Contact person's name"
                  required
                  style={{ borderRadius: 6 }}
                />
                {errors.contactPerson && <div style={{ color: 'red', marginTop: 4 }}>{errors.contactPerson}</div>}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label={<span style={labelStyle}>Designation <span style={{ color: 'red' }}>*</span></span>}>
                <Input 
                  prefix={<ApartmentOutlined />} 
                  name="designation"
                  value={designation}
                  onChange={handleChange}
                  placeholder="E.g., Manager, Coordinator"
                  required
                  style={{ borderRadius: 6 }}
                />
                {errors.designation && <div style={{ color: 'red', marginTop: 4 }}>{errors.designation}</div>}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label={<span style={labelStyle}>Description <span style={{ color: 'red' }}>*</span></span>}>
                <Input.TextArea
                  name="description"
                  value={description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Brief description of your incubator (max 500 characters)"
                  maxLength={500}
                  required
                  style={{ borderRadius: 6 }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {description.length}/500 characters
                </Typography.Text>
                {errors.description && <div style={{ color: 'red', marginTop: 4 }}>{errors.description}</div>}
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "24px 0" }} />

          {/* Location Information */}
          <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
            Location Information
          </Typography.Title>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label={<span style={labelStyle}>District <span style={{ color: 'red' }}>*</span></span>}>
                <Select
                  value={district}
                  onChange={(value) => setFormData({ ...formData, district: value })}
                  placeholder="Select district"
                  style={{ width: '100%', borderRadius: 6 }}
                  suffixIcon={<EnvironmentOutlined />}
                >
                  {districts.map((dist) => (
                    <Select.Option key={dist._id} value={dist.districtName}>
                      {dist.districtName}
                    </Select.Option>
                  ))}
                </Select>
                {errors.district && <div style={{ color: 'red', marginTop: 4 }}>{errors.district}</div>}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label={<span style={labelStyle}>Full Address <span style={{ color: 'red' }}>*</span></span>}>
                <Input.TextArea
                  name="address"
                  value={address}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Enter complete address"
                  required
                  style={{ borderRadius: 6 }}
                />
                {errors.address && <div style={{ color: 'red', marginTop: 4 }}>{errors.address}</div>}
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "24px 0" }} />

          {/* Contact Information */}
          <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
            Contact Information
          </Typography.Title>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={<span style={labelStyle}>Email Address <span style={{ color: 'red' }}>*</span></span>}>
                <Input 
                  prefix={<MailOutlined />} 
                  name="email"
                  type="email"
                  value={email}
                  onChange={handleChange}
                  placeholder="incubator@example.com"
                  required
                  style={{ borderRadius: 6 }}
                />
                {errors.email && <div style={{ color: 'red', marginTop: 4 }}>{errors.email}</div>}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={<span style={labelStyle}>Phone/Mobile <span style={{ color: 'red' }}>*</span></span>}>
                <Input 
                  prefix={<PhoneOutlined />} 
                  name="phone"
                  value={phone}
                  onChange={handleChange}
                  placeholder="+91 XXXXXXXXXX"
                  required
                  style={{ borderRadius: 6 }}
                />
                {errors.phone && <div style={{ color: 'red', marginTop: 4 }}>{errors.phone}</div>}
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label={<span style={labelStyle}>Website</span>}>
                <Input 
                  prefix={<GlobalOutlined />} 
                  name="website"
                  type="url"
                  value={website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  style={{ borderRadius: 6 }}
                />
                {errors.website && <div style={{ color: 'red', marginTop: 4 }}>{errors.website}</div>}
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "24px 0" }} />

          {/* Banner Upload */}
          <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
            Banner Image (Optional)
          </Typography.Title>

          <Form.Item style={{ marginBottom: 20 }}>
            <Upload
              listType="picture-card"
              showUploadList={false}
              beforeUpload={() => false}
              accept="image/*"
              maxCount={1}
              onChange={(info) => {
                const file = info.fileList[0]?.originFileObj;
                if (file) {
                  handleChange({ target: { name: 'banner', files: [file] } });
                }
              }}
            >
              {bannerPreview ? (
                <img src={bannerPreview} alt="banner" style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8 }} />
              ) : (
                uploadButton
              )}
            </Upload>
            <Typography.Text style={{ fontSize: 12, color: '#999' }}>
              Upload banner image (Max 10MB, Recommended: 1200x400px)
            </Typography.Text>
            {errors.banner && <div style={{ color: 'red', marginTop: 4 }}>{errors.banner}</div>}
          </Form.Item>

          <Divider style={{ margin: "24px 0" }} />

          {/* Account Security */}
          {!isAdminEdit && (
            <>
              <Typography.Title level={4} style={{ ...baseStyle, fontSize: 18, fontWeight: 500, marginBottom: 16, color: "#470172" }}>
                Account Security
              </Typography.Title>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label={<span style={labelStyle}>Password <span style={{ color: 'red' }}>*</span></span>}>
                    <Input.Password 
                      prefix={<LockOutlined />} 
                      name="password"
                      value={password}
                      onChange={handleChange}
                      placeholder="Create password"
                      required
                      minLength={6}
                      style={{ borderRadius: 6 }}
                    />
                    {password && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div style={{ flex: 1, height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ 
                              height: '100%', 
                              width: password.length >= 8 ? '100%' : password.length >= 6 ? '66%' : '33%',
                              background: password.length >= 8 ? '#52c41a' : password.length >= 6 ? '#faad14' : '#ff4d4f',
                              transition: 'all 0.3s'
                            }} />
                          </div>
                          <Typography.Text style={{ 
                            fontSize: 12,
                            color: password.length >= 8 ? '#52c41a' : password.length >= 6 ? '#faad14' : '#ff4d4f'
                          }}>
                            {password.length >= 8 ? 'Strong' : password.length >= 6 ? 'Medium' : 'Weak'}
                          </Typography.Text>
                        </div>
                      </div>
                    )}
                    {errors.password && <div style={{ color: 'red', marginTop: 4 }}>{errors.password}</div>}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={<span style={labelStyle}>Confirm Password <span style={{ color: 'red' }}>*</span></span>}>
                    <Input.Password 
                      prefix={<LockOutlined />} 
                      name="password2"
                      value={password2}
                      onChange={handleChange}
                      placeholder="Re-enter password"
                      required
                      minLength={6}
                      style={{ borderRadius: 6 }}
                    />
                    {password2 && (
                      <Typography.Text style={{ 
                        fontSize: 12, 
                        color: password === password2 ? '#52c41a' : '#ff4d4f',
                        display: 'block',
                        marginTop: 8
                      }}>
                        {password === password2 ? '✓ Passwords match' : '✗ Passwords do not match'}
                      </Typography.Text>
                    )}
                    {errors.password2 && <div style={{ color: 'red', marginTop: 4 }}>{errors.password2}</div>}
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ background: '#f0e6ff', padding: 16, borderRadius: 8, marginBottom: 20 }}>
                <Typography.Title level={5} style={{ ...baseStyle, fontSize: 14, fontWeight: 500, color: "#470172", marginBottom: 8 }}>
                  Account Information
                </Typography.Title>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#666' }}>
                  <li>Your account will be reviewed by our admin team</li>
                  <li>You'll receive an email once your account is approved</li>
                  <li>This process typically takes 1-2 business days</li>
                </ul>
              </div>

              <Divider style={{ margin: "24px 0" }} />

              <Form.Item style={{ marginBottom: 20 }}>
                <Checkbox required>
                  I agree to the <a href="/terms" style={{ color: '#470172' }}>Terms of Service</a> and <a href="/privacy" style={{ color: '#470172' }}>Privacy Policy</a>
                </Checkbox>
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              block
              size="large"
              style={{ 
                background: 'linear-gradient(to right, #672f89, #8e44ad)', 
                borderColor: '#672f89',
                borderRadius: 8,
                height: 48,
                fontSize: 16,
                fontWeight: 500
              }}
            >
              {loading ? 'Processing...' : 'Submit for Approval'}
            </Button>
          </Form.Item>

          {!isAdminEdit && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Typography.Text>
                Already have an account? <a href="/login" style={{ color: '#470172' }}>Sign in here</a>
              </Typography.Text>
            </div>
          )}
        </Form>
        </form>
      </Card>
    </div>
  );
};

export default IncubatorRegister;